import { useState, useEffect, useCallback } from "react";

/**
 * Enhanced useKeyPress hook that resets key state when window loses focus.
 * This prevents "stuck" key states when users switch windows while holding a key.
 *
 * The standard react-use useKeyPress hook doesn't handle window blur events,
 * which causes keys to appear "stuck" if released while another window has focus.
 */
export const useKeyPressWithReset = (targetKey: string): boolean => {
  const [isPressed, setIsPressed] = useState(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        setIsPressed(true);
      }
    },
    [targetKey]
  );

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === targetKey) {
        setIsPressed(false);
      }
    },
    [targetKey]
  );

  const handleBlur = useCallback(() => {
    // Reset key state when window loses focus
    setIsPressed(false);
  }, []);

  const handleVisibilityChange = useCallback(() => {
    // Reset when tab becomes hidden
    if (document.hidden) {
      setIsPressed(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleKeyDown, handleKeyUp, handleBlur, handleVisibilityChange]);

  return isPressed;
};
